/**
 * Keystroke - Compact keyboard key chip used in keybind hints and controls
 * panels. Sizes: small | medium | large.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type KeystrokeSize = 'small' | 'medium' | 'large';

export interface KeystrokeOptions extends BaseUIComponentOptions {
  /** Key label shown inside the chip (e.g. "W", "SHIFT", "F"). */
  key?: string;
  /** Size preset. */
  size?: KeystrokeSize;
}

export class Keystroke extends BaseUIComponent<KeystrokeOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Keystroke',
    category: 'data',
    summary: 'Keyboard key chip used in keybind hints and controls panels.',
    useCases: ['keybind hint', 'shortcut chip', 'controls panel', 'tutorial overlay'],
    optionsType: 'KeystrokeOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/keystroke.html',
      styles: '@engine/assets/ui/styles/keystroke.css',
    },
  };

  private rootElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Keystroke.metadata.assetPaths.template,
      stylesPath: Keystroke.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<KeystrokeOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      key: 'W',
      size: 'medium',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      key: this.options.key,
      size: this.options.size,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-keystroke') as HTMLElement | null;
  }

  /** Update the key label. */
  public setKey(key: string): void {
    this.options.key = key;
    if (this.rootElement) this.rootElement.textContent = key;
  }

  /** Swap the size preset. */
  public setSize(size: KeystrokeSize): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`size-${this.options.size}`);
    this.options.size = size;
    this.rootElement.classList.add(`size-${size}`);
  }

  protected override onDestroy(): void {
    this.rootElement = null;
  }
}
